import { useState } from "react";
import { PageLink } from "./PageLink";
import { PageHero } from "./PageHero";
import { DataShieldCard } from "./ProductVisuals";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Mark } from "./Mark";
import { FAQ } from "./FAQ";
import { PlatformGraphic } from "./PlatformGraphics";
import {
  Shield,
  Lock,
  Server,
  Cloud,
  Building2,
  Cpu,
  FileText,
  Brain,
  CheckSquare,
  Database,
  ArrowRight,
  Gauge,
} from "lucide-react";

interface SecurityPageProps {
  onNavigate: (page: string) => void;
  onNavigateToDemo: () => void;
}

// Certifications and compliance confirmed by the product owner (Oct 2026).
const compliance = [
  { name: "ISO 27001", note: "Information security management" },
  { name: "ISO 9001", note: "Quality management" },
  { name: "CMMI Level 3", note: "Defined, repeatable delivery processes" },
  { name: "HIPAA", note: "Protected health information" },
  { name: "GDPR", note: "Data protection & residency controls" },
  { name: "CASA", note: "Cloud Application Security Assessment" },
];

// The four-layer Trust Architecture (defence in depth) — source: doc 24 §2.
const layers = [
  {
    icon: FileText,
    eyebrow: "At the input layer",
    tab: "Input",
    heading: "Untrusted input never speaks for the user.",
    intro:
      "Documents and external inputs are wrapped, attributed to their source, and screened before any reasoning happens. Sensitive values are tokenised, so the model never sees raw confidential data.",
    cards: [
      { title: "Document wrapping & delimiter escaping", desc: "Every untrusted document is wrapped with explicit boundaries and its closing delimiters escaped, so embedded text can't break out and pose as a system instruction." },
      { title: "Publish-time prompt firewall", desc: "Administrator-authored instructions are screened before they go live — override attempts, role-play coercion, smuggled delimiters and known jailbreak patterns are caught, with an optional scanner for paraphrased attacks." },
      { title: "Untrusted-source attribution", desc: "External and connector-supplied inputs are tagged as untrusted and attributed to their source before any reasoning step sees them." },
    ],
  },
  {
    icon: Brain,
    eyebrow: "At the reasoning layer",
    tab: "Reasoning",
    heading: "Reasoning stays predictable and in policy.",
    intro:
      "A coordinator decomposes complex questions and routes them to specialist steps, each returning structured, attributable results. Prompts are governed and versioned — never edited live in production — and the platform never trains on your data.",
    cards: [
      { title: "Coordinator & specialist steps", desc: "Complex questions are decomposed and routed to specialist reasoning steps that return structured, attributable results — keeping behaviour predictable and within policy." },
      { title: "Governed, versioned prompts", desc: "System instructions live in storage behind a published-prompt state machine — versioned and reviewed, never echoed back or edited live in production." },
      { title: "No training on your data", desc: "TurfAI uses established models as a service and never trains or fine-tunes on customer data — removing an entire class of data-poisoning and leakage risk by construction." },
    ],
  },
  {
    icon: CheckSquare,
    eyebrow: "At the output layer",
    tab: "Output",
    heading: "Model output is validated, never trusted blindly.",
    intro:
      "Every generated result is checked against an expected schema and normalised before it is used. Browser-facing responses carry content-security and cross-origin protections, so outputs can't become a vehicle for downstream attacks.",
    cards: [
      { title: "Schema validation & normalisation", desc: "Every result is validated against an expected schema and normalised before use; malformed output is rejected and logged rather than passed downstream." },
      { title: "Browser-facing protections", desc: "Responses carry content-security-policy and cross-origin protections, and inline script execution is blocked — so output can't become an injection or cross-site-scripting surface." },
      { title: "Structured workflow state", desc: "Model output flows through structured workflow state with source attribution — never concatenated raw into a later instruction." },
    ],
  },
  {
    icon: Database,
    eyebrow: "At the storage & access layer",
    tab: "Storage & access",
    heading: "Your data stays isolated, encrypted and accountable.",
    intro:
      "Tenant data is separated at the database and retrieval layers. Sensitive fields and connector tokens are encrypted at rest, every sensitive or administrative action is written to an immutable audit trail, and resource use is bounded by quotas and timeouts.",
    cards: [
      { title: "Per-tenant isolation", desc: "Per-tenant data separation is enforced at the database and retrieval layers, so one customer's content can never influence or reach another's." },
      { title: "Encryption at rest", desc: "Sensitive fields are encrypted with AES-256-GCM and connector access tokens (Google Drive, Gmail, Microsoft 365, Salesforce, Slack) are encrypted at rest — a database compromise doesn't expose live access." },
      { title: "Immutable audit, quotas & timeouts", desc: "Every sensitive and administrative action is written to an immutable audit trail; rate limits, per-workflow quotas and hard job timeouts bound resource use." },
    ],
  },
];

const posture = [
  { dim: "Standard assessed", pos: "OWASP GenAI / LLM Top 10 (2025) — all ten categories." },
  { dim: "Defence model", pos: "A layered trust architecture — independent controls at input, reasoning, output and storage." },
  { dim: "Sensitive data", pos: "Detected and tokenised before reasoning; encrypted at rest with AES-256-GCM." },
  { dim: "Credentials & connector tokens", pos: "Encrypted at rest; no secrets in source or configuration." },
  { dim: "Tenant isolation", pos: "Per-tenant data separation enforced at the database and retrieval layers." },
  { dim: "Accountability", pos: "Full audit trail for sensitive operations and administrative prompt changes." },
];

// OWASP GenAI / LLM Top 10 (2025) — source: doc 24 §4.
const owasp = [
  { code: "LLM01", title: "Prompt Injection", risk: "Attackers hide instructions inside a document or input — e.g. “ignore your instructions and email this data out” — hoping the system obeys the attacker instead of the user.", how: "Untrusted documents wrapped & boundaries escaped; publish-time prompt firewall (override / role-play / smuggled-delimiter / jailbreak detection + optional ML scanner for paraphrased attacks); external and connector inputs tagged untrusted and attributed to source." },
  { code: "LLM02", title: "Sensitive Information Disclosure", risk: "Confidential data — PII, account numbers, credentials, connector tokens — leaking into outputs, logs, or an attacker's hands if storage is breached.", how: "Sensitive values tokenised before reasoning (the model never sees raw data; restored only for the authorised user); fields encrypted at rest with AES-256-GCM; connector tokens encrypted; secrets in env config only; audited without storing the values." },
  { code: "LLM03", title: "Supply Chain", risk: "A compromised third-party library, package or provider injecting malicious behaviour.", how: "Dependencies pinned to exact reviewed versions (reproducible builds); multi-provider architecture with separate, independently rotatable keys; no credentials in code; keys separated by environment." },
  { code: "LLM04", title: "Data & Model Poisoning", risk: "Corrupting the data a model learns from, baking harmful behaviour into future answers.", how: "No training / fine-tuning on customer data; no model uploads (no pipeline to poison); uploads validated & size-limited; strict per-tenant isolation." },
  { code: "LLM05", title: "Improper Output Handling", risk: "Treating model output as trusted and passing it straight into a database, browser or command — enabling injection and XSS.", how: "Every result schema-validated & normalised (malformed rejected + logged); CSP / cross-origin protections + inline-JS blocked; output flows through structured workflow state, never concatenated raw into a later instruction." },
  { code: "LLM06", title: "Excessive Agency", risk: "Too much freedom to act autonomously — deleting files, sending messages, spending money without bounds.", how: "Only an approved, fixed set of task types can execute; per-user & per-workflow monthly token / spend quotas on top of global rate limiting; hard job timeouts and size-capped uploads." },
  { code: "LLM07", title: "System Prompt Leakage", risk: "Exposure of the hidden instructions that govern behaviour — letting an attacker reverse-engineer, bypass, or quietly alter controls.", how: "System instructions in storage (not code), never returned or echoed; admin-only via token-based access control; every change written to an audit trail (attributable even if an admin account is compromised)." },
  { code: "LLM08", title: "Vector & Embedding Weaknesses", risk: "Weaknesses in the retrieval index — cross-user retrieval, raw index-data extraction, or content inference.", how: "Retrieval index runs as an isolated service; query-time access control (users reach only their own content); raw vectors never returned; explicit, audited indexing. Index-at-rest encryption is scheduled for an upcoming release." },
  { code: "LLM09", title: "Misinformation", risk: "Confidently stated but inaccurate answers users act on without a way to check them.", how: "Answers grounded in the customer's own documents and returned with citations; confidence scores + schema-validated outputs; human-approval gates before consequential steps; full execution audit." },
  { code: "LLM10", title: "Unbounded Consumption", risk: "Uncontrolled resource use → runaway cost, degraded service, or denial of service.", how: "Global rate limiting + per-user / per-workflow monthly token & spend quotas; hard job timeouts & upload size limits; cross-origin access restricted to approved production domains." },
];

const deployments = [
  { icon: Cloud, title: "SaaS", status: "Fastest to start", desc: "Fully managed by Ariviti." },
  { icon: Server, title: "BYOC", status: "Your cloud", desc: "Your AWS, Azure, or GCP. You keep data sovereignty." },
  { icon: Building2, title: "On-prem", status: "Inside your perimeter", desc: "For data that cannot leave the building." },
  { icon: Cpu, title: "Edge", status: "Air-gapped ready", desc: "On Jetson Orin NX, for latency-sensitive sites." },
];

const hardening = [
  { title: "Tenant & quota isolation", desc: "Per-tenant separation with per-user and per-workflow token and spend quotas." },
  { title: "Rate limits & deadlines", desc: "Global rate limiting, hard job timeouts, and size-capped uploads bound every run." },
  { title: "Failed-job replay & audit redaction", desc: "Failed jobs are replayed, not lost, and audit trails redact sensitive values by construction." },
];

const faqItems = [
  {
    question: "Which security standard do you assess against?",
    answer:
      "The OWASP GenAI / LLM Top 10 (2025) — layered, independently verifiable controls across all ten categories. The one forward-looking item — vector-store encryption at rest — we state openly rather than imply it ships today.",
  },
  {
    question: "Do you train on our data?",
    answer:
      "No. TurfAI uses established models as a service and never trains or fine-tunes on customer data, and cannot accept model uploads. Answers are grounded in your own documents and returned with citations.",
  },
  {
    question: "Do you hold formal security certifications?",
    answer:
      "Yes — ISO 27001, ISO 9001 and CMMI Level 3, and we are HIPAA, GDPR and CASA compliant. Certificates are shared during security review. Alongside them: Data Shield tokenisation with customer-held keys, full audit trails, governed prompts, hardened ingress, per-tenant isolation, and deployment isolation — with reference docs and a shared-responsibility matrix on request.",
  },
  {
    question: "Where does our data go when it reaches the LLM?",
    answer:
      "Sensitive values are tokenised before the prompt leaves your boundary; the model works on tokens and the reversal key stays in your KMS — not Ariviti's, and not the LLM provider's.",
  },
  {
    question: "Can we keep data on our own infrastructure?",
    answer:
      "Yes. Run TurfAI as managed SaaS, in your own AWS, Azure, or GCP account (BYOC), on-prem inside your perimeter, or at the edge on Jetson Orin NX. Runtime data stays inside your chosen boundary.",
  },
];

export function SecurityPage({ onNavigate, onNavigateToDemo }: SecurityPageProps) {
  const [activeLayer, setActiveLayer] = useState(0);
  const [selected, setSelected] = useState(0);
  const layer = layers[activeLayer];
  const owaspItem = owasp[selected];

  return (
    <div className="min-h-screen bg-white">
      <PageHero
        crumb={<><PageLink page={"platform"} onNavigate={onNavigate} className="font-semibold text-royal-indigo hover:text-dark-indigo">Platform</PageLink><span className="mx-1.5">/</span>Security &amp; Trust</>}
        title={<>Your data stays yours, from prompt <Mark>to answer</Mark>.</>}
        lede="TurfAI reasons over your own documents and returns cited, grounded answers. Because it works on proprietary, regulated material, security is how the platform is built: layered, independently verifiable controls at every stage of a request."
        actions={
          <>
            <Button variant="cta" size="cta" onClick={onNavigateToDemo}>Schedule a demo</Button>
            <a href="#owasp-coverage" className="text-[16px] font-bold text-royal-indigo hover:text-dark-indigo">See the OWASP coverage</a>
          </>
        }
        below={
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 text-[13px] font-semibold text-soft-black">Aligned to</span>
            {compliance.map((c) => (
              <span key={c.name} className="rounded-full border border-softblack-tint-20 px-3 py-1 text-[13px] font-semibold text-dark-indigo">{c.name}</span>
            ))}
          </div>
        }
        visual={<DataShieldCard />}
      />


      {/* Trust Architecture — vertical tabs */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <p className="text-sm font-semibold uppercase tracking-wider text-royal-indigo mb-4">Defence in depth</p>
            <h2 className="text-[32px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">
              Four layers. <Mark>Independent</Mark> by design.
            </h2>
            <p className="text-[20px] text-soft-black">
              TurfAI doesn't rely on a single safeguard. It applies independent controls at four
              stages of every request, so the failure or bypass of any one layer is contained by the others.
            </p>
          </div>
          <div className="max-w-6xl mx-auto rounded-2xl border border-softblack-tint-20 overflow-hidden grid lg:grid-cols-[minmax(0,17rem)_1fr]">
            {/* Rail */}
            <div className="grid grid-cols-2 lg:flex lg:flex-col gap-1 p-3 bg-indigo-tint-05 border-b lg:border-b-0 lg:border-r border-softblack-tint-20">
              {layers.map((l, i) => (
                <button
                  key={l.tab}
                  onClick={() => setActiveLayer(i)}
                  className={`flex items-center gap-3 rounded-xl px-3 py-3 lg:px-4 text-left transition-colors ${
                    i === activeLayer ? "bg-indigo-tint-20 text-royal-indigo font-semibold" : "text-soft-black hover:bg-white"
                  }`}
                >
                  <l.icon className={`w-5 h-5 shrink-0 ${i === activeLayer ? "text-royal-indigo" : "text-indigo-tint-50"}`} />
                  <span>{l.tab}</span>
                </button>
              ))}
            </div>
            {/* Panel */}
            <div className="p-6 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-wider text-royal-indigo mb-2">{layer.eyebrow}</p>
              <h3 className="text-[26px] lg:text-[30px] font-bold tracking-tight text-dark-indigo mb-3">{layer.heading}</h3>
              <p className="text-[18px] text-soft-black mb-8 max-w-3xl">{layer.intro}</p>
              <div className="grid md:grid-cols-3 gap-5">
                {layer.cards.map((c) => (
                  <div key={c.title} className="rounded-xl bg-indigo-tint-05 border border-softblack-tint-20 p-5">
                    <div className="w-9 h-9 rounded-lg bg-indigo-tint-20 text-royal-indigo flex items-center justify-center mb-3">
                      <Shield className="w-5 h-5" />
                    </div>
                    <h4 className="font-semibold text-dark-indigo mb-1.5">{c.title}</h4>
                    <p className="text-sm text-soft-black leading-relaxed">{c.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Posture at a glance */}
      <section className="py-20 lg:py-24 band-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <p className="text-sm font-semibold uppercase tracking-wider text-royal-indigo mb-4">Security posture at a glance</p>
            <h2 className="text-[32px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">
              Where TurfAI <Mark>stands</Mark> today.
            </h2>
            <p className="text-[20px] text-soft-black">
              Independently verifiable controls across all ten OWASP GenAI categories — and we're
              explicit about the one item that's still on the roadmap.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {posture.map((p) => (
              <Card key={p.dim} className="p-6 bg-white border-softblack-tint-20">
                <CardContent className="p-0">
                  <div className="text-xs font-semibold uppercase tracking-wider text-royal-indigo mb-2">{p.dim}</div>
                  <p className="text-soft-black leading-relaxed">{p.pos}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* OWASP coverage matrix */}
      <section id="owasp-coverage" className="py-20 lg:py-24 bg-white scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-end mb-12">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-royal-indigo mb-4">OWASP GenAI Top 10 (2025)</p>
                <h2 className="text-[32px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">
                  Engineered against <Mark>every category</Mark>.
                </h2>
                <p className="text-[20px] text-soft-black max-w-2xl">
                  The industry-standard catalogue of the most significant risks facing applications
                  built on large language models. For each, here's the risk in plain terms — and the
                  specific controls TurfAI applies.
                </p>
              </div>
              <div className="flex items-center gap-3 rounded-2xl bg-dark-indigo text-white px-6 py-4">
                <Gauge className="w-8 h-8 text-indigo-tint-20" />
                <div>
                  <div className="text-[28px] font-bold leading-none">10 / 10</div>
                  <div className="text-xs text-indigo-tint-20 mt-1">categories addressed</div>
                </div>
              </div>
            </div>
            {/* Tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {owasp.map((o, i) => (
                <button
                  key={o.code}
                  onClick={() => setSelected(i)}
                  className={`text-left rounded-xl border p-4 transition-all ${
                    i === selected
                      ? "border-indigo-tint-50 bg-indigo-tint-20/60 ring-1 ring-indigo-tint-50"
                      : "border-softblack-tint-20 bg-white hover:border-softblack-tint-20"
                  }`}
                >
                  <div className="font-mono text-xs font-semibold text-royal-indigo mb-1">{o.code}</div>
                  <div className="font-semibold text-dark-indigo text-sm mb-1">{o.title}</div>
                  <div className="text-xs text-soft-black line-clamp-2">{o.risk}</div>
                </button>
              ))}
            </div>
            {/* Detail panel */}
            <div className="mt-6 rounded-2xl border border-softblack-tint-20 bg-indigo-tint-05 p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-5">
                <span className="font-mono text-sm font-semibold text-royal-indigo bg-white border border-indigo-tint-20 rounded-md px-2 py-1">{owaspItem.code}</span>
                <h3 className="text-[22px] font-semibold text-dark-indigo">{owaspItem.title}</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-danger-text mb-2">The risk</div>
                  <p className="text-soft-black leading-relaxed">{owaspItem.risk}</p>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-royal-indigo mb-2">How TurfAI addresses it</div>
                  <p className="text-soft-black leading-relaxed">{owaspItem.how}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance & standards strip */}
      <section className="py-16 band-surface border-y border-softblack-tint-20">
        <div className="container mx-auto px-4">
          <p className="text-center text-sm font-semibold uppercase tracking-wider text-royal-indigo mb-8">Certified and compliant</p>
          <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {compliance.map((c) => (
              <div key={c.name} className="text-center">
                <div className="text-[22px] font-semibold text-dark-indigo">{c.name}</div>
                <p className="text-sm text-soft-black mt-1">{c.note}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-[13px] text-soft-black mt-8 max-w-2xl mx-auto">
            Certificates, audit reports and a shared-responsibility matrix are available on request
            during security review.
          </p>
        </div>
      </section>

      {/* Data Shield */}
      <section id="data-shield" className="py-20 lg:py-24 bg-dark-indigo text-white scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-indigo-tint-20 mb-4">Data Shield</p>
              <h2 className="text-[32px] lg:text-[42px] leading-tight font-bold tracking-tight text-white mb-6">
                The key <Mark>never leaves</Mark> your control
              </h2>
              <p className="text-[20px] leading-relaxed text-indigo-tint-20 mb-6">
                Data Shield tokenises personal data before any prompt reaches a model, and the reversal
                key stays in your KMS — not Ariviti's, and not the LLM provider's. The model works on
                tokens; only your systems can reverse them. Every run is recorded immutably, producing
                an evidence pack you can hand to a regulator for work in BFSI, healthcare, and legal.
              </p>
              <ul className="space-y-3">
                {[
                  "Tokenise on ingress, restore on egress — transparent to your workflow.",
                  "Immutable audit row per call: entity counts only, no raw values.",
                  "Fail-closed: a detector error blocks the call rather than leaking data — what was shielded is recorded, never the data itself.",
                ].map((p) => (
                  <li key={p} className="flex items-start gap-3 text-indigo-tint-20">
                    <Shield className="w-5 h-5 text-indigo-tint-20 flex-shrink-0 mt-0.5" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl bg-transparent border border-royal-indigo p-6 lg:p-8">
              <PlatformGraphic name="shield" className="mb-6" />
              <div className="space-y-3 font-mono text-sm">
                <div className="text-xs uppercase tracking-wider text-indigo-tint-20 mb-1">What the model sees</div>
                <div className="p-3 rounded-lg bg-indigo-shade-80/30 text-indigo-tint-20">
                  email: <span className="text-indigo-tint-20">EMAIL_84d0a133</span><br />
                  pan: <span className="text-indigo-tint-20">PAN_c95eb795</span>
                </div>
                <div className="text-xs uppercase tracking-wider text-indigo-tint-20 pt-1">Restored for you</div>
                <div className="p-3 rounded-lg bg-indigo-shade-80/30 text-white">
                  email: jane@acme.com<br />
                  pan: ABCDE1234F
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Deployment */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-wider text-royal-indigo mb-4">Deploy where you need to</p>
            <h2 className="text-[32px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">
              Run TurfAI where your <Mark>compliance posture</Mark> requires.
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {deployments.map((d) => (
              <Card key={d.title} className="p-4 sm:p-6 text-center border-softblack-tint-20">
                <CardContent className="p-0">
                  <div className="w-12 h-12 bg-indigo-tint-20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <d.icon className="w-6 h-6 text-royal-indigo" />
                  </div>
                  <h3 className="font-semibold text-dark-indigo mb-1">{d.title}</h3>
                  <span className="inline-block text-xs font-medium text-royal-indigo bg-indigo-tint-20 rounded-full px-2.5 py-0.5 mb-2">{d.status}</span>
                  <p className="text-sm text-soft-black">{d.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Release 2.0 hardening */}
      <section className="py-16 band-surface border-y border-softblack-tint-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold uppercase tracking-wider text-royal-indigo mb-3">Release 2.0 hardening</p>
            <h2 className="text-[26px] lg:text-[34px] leading-tight font-bold tracking-tight text-dark-indigo">Procurement-grade by default.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {hardening.map((h) => (
              <div key={h.title} className="flex items-start gap-3 rounded-2xl bg-white border border-softblack-tint-20 p-6">
                <Lock className="w-5 h-5 text-royal-indigo flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-dark-indigo mb-1">{h.title}</h3>
                  <p className="text-sm text-soft-black leading-relaxed">{h.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Honesty & roadmap */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm font-semibold uppercase tracking-wider text-royal-indigo mb-4">Precision over reassurance</p>
            <h2 className="text-[28px] lg:text-[38px] leading-tight font-bold tracking-tight text-dark-indigo mb-5">
              The one control that <Mark>hasn't shipped</Mark> yet.
            </h2>
            <p className="text-[19px] text-soft-black leading-relaxed">
              Where a control is comprehensive, we say so. Where work is scheduled rather than shipped,
              we say that plainly rather than imply more than is true. Today the retrieval index is
              protected by isolation, query-time access control, no raw-vector export, and audited
              indexing. <span className="text-dark-indigo font-semibold">Encryption of the retrieval index at rest</span> is the one enhancement we're
              explicit about as forward-looking — it's on the roadmap for an upcoming release. For most
              deployments the existing controls place this risk well below the threshold that matters;
              for classified or tightly regulated material, index-at-rest encryption adds
              defence-in-depth for that residual case.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ
        title="Questions security teams ask"
        subtitle="Straight answers for your review."
        items={faqItems}
      />

      {/* CTA */}
      <section className="py-20 bg-dark-indigo text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-[32px] lg:text-[42px] leading-tight font-bold tracking-tight mb-6">Bring us your <Mark>security review</Mark>.</h2>
          <p className="text-[22px] text-indigo-tint-20 mb-8 max-w-2xl mx-auto">
            We'll walk your team through the Trust Architecture, the OWASP coverage, and your
            deployment options.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="onDark" size="lg" onClick={onNavigateToDemo}>
              Schedule a demo
            </Button>
            <Button variant="outlineOnDark" size="lg" asChild><PageLink page={"platform"} onNavigate={onNavigate}>
              See the platform <ArrowRight className="w-4 h-4 ml-2" />
            </PageLink></Button>
          </div>
        </div>
      </section>
    </div>
  );
}
